import { ReactNode, useEffect, useMemo } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import i18n from 'i18next'
import { lightTheme, darkTheme } from './theme'
import { useThemeMode } from './ThemeContext'
import { useSettingsStore, LANG_DIRECTION } from '../store/settingsStore'

const ltrCache = createCache({ key: 'mui', stylisPlugins: [prefixer] })
const rtlCache = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] })

/**
 * Drives theme direction and the emotion style cache from the selected
 * language. RTL languages (HE/AR) get an emotion cache with
 * stylis-plugin-rtl so *physical* CSS (margins, drawer anchor, paddings)
 * mirrors automatically — not just MUI's conditional logic. Without the
 * RTL cache the sidebar and content margins only half-flip.
 */
export function DirectionProvider({ children }: { children: ReactNode }) {
  const { mode } = useThemeMode()
  const language = useSettingsStore((s) => s.language)
  const direction = LANG_DIRECTION[language] ?? 'ltr'

  useEffect(() => {
    document.documentElement.dir = direction
    document.documentElement.lang = language.toLowerCase()
    if (i18n.language !== language.toLowerCase()) {
      i18n.changeLanguage(language.toLowerCase())
    }
  }, [direction, language])

  const themed = useMemo(() => {
    const base = mode === 'dark' ? darkTheme : lightTheme
    return { ...base, direction }
  }, [mode, direction])

  const cache = direction === 'rtl' ? rtlCache : ltrCache

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={themed}>{children}</MuiThemeProvider>
    </CacheProvider>
  )
}
